import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Calendar, MessageSquare, Smile } from "lucide-react";

// Snowflake component
const Snowflake = ({ style, delay, duration, size }) => (
  <motion.div
    className="absolute rounded-full bg-white pointer-events-none"
    style={{
      width: size,
      height: size,
      left: style.left,
      top: -20,
      opacity: 0.6,
    }}
    animate={{
      y: ["0vh", "120vh"],
      x: [0, Math.random() * 50 - 25],
      opacity: [0, 0.8, 0.6, 0],
      rotate: [0, 360],
    }}
    transition={{
      duration: duration,
      delay: delay,
      repeat: Infinity,
      ease: "linear",
    }}
  />
);

const ProcessSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  const steps = [
    {
      number: "01",
      icon: Calendar,
      title: "Book a Consultation",
      description: "Schedule a call or online booking to discuss your property needs.",
    },
    {
      number: "02",
      icon: MessageSquare,
      title: "Tell Matthew Your Needs",
      description: "Choose your services and priorities. We'll create a custom plan together.",
    },
    {
      number: "03",
      icon: Smile,
      title: "Relax",
      description: "Matthew handles everything, keeping you updated along the way.",
    },
  ];

  // Generate snowflakes
  const snowflakes = Array.from({ length: 50 }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    delay: Math.random() * 10,
    duration: Math.random() * 10 + 8,
    size: Math.random() * 4 + 2,
  }));

  return (
    <section
      id="process"
      ref={sectionRef}
      className="py-24 md:py-32 bg-navy text-white relative overflow-hidden"
      data-testid="process-section"
    >
      {/* Snowfall Animation */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {snowflakes.map((flake) => (
          <Snowflake
            key={flake.id}
            style={{ left: flake.left }}
            delay={flake.delay}
            duration={flake.duration}
            size={flake.size}
          />
        ))}
      </div>

      {/* Gradient Overlay for depth */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-navy/50 to-navy pointer-events-none" />

      {/* Mountain silhouette at bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-32 opacity-10">
        <svg viewBox="0 0 1440 320" className="w-full h-full" preserveAspectRatio="none">
          <path
            fill="white"
            d="M0,224L48,213.3C96,203,192,181,288,181.3C384,181,480,203,576,224C672,245,768,267,864,261.3C960,256,1056,224,1152,197.3C1248,171,1344,149,1392,138.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
          />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <span className="text-accent-light text-sm font-semibold tracking-wider uppercase">
            How It Works
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-white mt-4 mb-6 leading-tight">
            Simple & <span className="italic">Seamless</span>
          </h2>
          <p className="text-gray-300 text-base md:text-lg">
            Getting started is easy. Here's how we'll work together.
          </p>
        </motion.div>

        {/* Steps */}
        <div className="grid md:grid-cols-3 gap-8 md:gap-12">
          {steps.map((step, index) => (
            <motion.div
              key={step.number}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 * index }}
              className="relative text-center"
              data-testid={`process-step-${index}`}
            >
              {/* Connector Line */}
              {index < 2 && (
                <motion.div 
                  className="hidden md:block absolute top-12 left-[60%] w-[80%] h-px bg-white/20"
                  initial={{ scaleX: 0 }}
                  animate={isInView ? { scaleX: 1 } : {}}
                  transition={{ duration: 0.8, delay: 0.5 + index * 0.2 }}
                  style={{ originX: 0 }}
                />
              )}
              
              {/* Icon Circle with glow */}
              <motion.div
                initial={{ scale: 0 }}
                animate={isInView ? { scale: 1 } : {}}
                transition={{ duration: 0.5, delay: 0.3 + 0.2 * index }}
                className="relative w-24 h-24 mx-auto mb-6"
              >
                {/* Glow effect */}
                <motion.div
                  className="absolute inset-0 rounded-full bg-white/20 blur-xl"
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.3, 0.5, 0.3],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    delay: index * 0.5,
                  }}
                />
                <div className="w-full h-full bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center relative border border-white/20">
                  <step.icon className="w-10 h-10 text-white" strokeWidth={1.5} />
                  <span className="absolute -top-2 -right-2 w-8 h-8 bg-accent text-white text-sm font-bold rounded-full flex items-center justify-center shadow-lg">
                    {step.number}
                  </span>
                </div>
              </motion.div>

              {/* Content */}
              <h3 className="font-heading text-xl md:text-2xl text-white mb-3">
                {step.title}
              </h3>
              <p className="text-gray-300 text-sm md:text-base leading-relaxed max-w-xs mx-auto">
                {step.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="text-center mt-16"
        >
          <motion.a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="inline-flex items-center justify-center bg-white text-navy px-8 py-4 rounded-lg text-base font-semibold hover:bg-gray-100 transition-all duration-300 hover:shadow-xl"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            data-testid="process-cta-btn"
          >
            Get Started Today
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default ProcessSection;